import { useCallback, useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ErrorRecoveryDemoVoice.module.css';

type Who = 'you' | 'agent' | 'cue';
type Step = 'choose' | 'alerted' | 'reminded' | 'done';

interface Line {
  id: number;
  who: Who;
  text: string;
}

type Script = Array<Omit<Line, 'id'>>;

const speakMs = (text: string) => Math.min(3200, 900 + text.length * 22);

const FEEDBACK: Record<Step, string> = {
  choose: 'One part worked, one did not. Pick a reply - or try retrying to hear why that will not help.',
  alerted: 'The failure became a small task on your phone. Ask about the door to check the agent stays honest.',
  reminded: 'The agent will follow up. Skip ahead to hear the reminder.',
  done: 'You always knew what succeeded, what failed and what to do next. Press Reset to try another path.',
};

export function ErrorRecoveryDemoVoice() {
  const [lines, setLines] = useState<Line[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const [busy, setBusy] = useState(true);
  const [step, setStep] = useState<Step>('choose');
  const [retried, setRetried] = useState(false);
  const [heatSet, setHeatSet] = useState(false);
  const timers = useRef<number[]>([]);
  const nextId = useRef(0);

  const play = useCallback((script: Script, next: Step, onLine?: (index: number) => void) => {
    setBusy(true);
    let t = 0;
    const at = (ms: number, fn: () => void) => timers.current.push(window.setTimeout(fn, ms));
    script.forEach((item, i) => {
      t += item.who === 'you' ? (i === 0 ? 0 : 400) : item.who === 'cue' ? 500 : 700;
      at(t, () => {
        setLines((l) => [...l, { ...item, id: nextId.current++ }]);
        onLine?.(i);
      });
      if (item.who === 'agent') {
        at(t, () => setSpeaking(true));
        t += speakMs(item.text);
        at(t, () => setSpeaking(false));
      }
    });
    at(t + 200, () => {
      setStep(next);
      setBusy(false);
    });
  }, []);

  useEffect(() => {
    play(
      [
        { who: 'you', text: 'Set the heat to 68 and lock the front door.' },
        {
          who: 'agent',
          text: "Heat is set to 68. I couldn't lock the front door - the lock isn't responding, and its battery was low yesterday.",
        },
        { who: 'agent', text: 'I can send an alert to your phone, or remind you to check it in ten minutes.' },
      ],
      'choose',
      (i) => {
        if (i === 1) setHeatSet(true);
      }
    );
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.length = 0;
    };
  }, [play]);

  const sendAlert = () =>
    play(
      [
        { who: 'you', text: 'Send it to my phone.' },
        { who: 'cue', text: '(soft chime)' },
        { who: 'agent', text: "Sent. The alert has a button to lock it once the battery's replaced." },
      ],
      'alerted'
    );

  const remind = () =>
    play(
      [
        { who: 'you', text: 'Remind me in ten minutes.' },
        { who: 'agent', text: "Okay, I'll remind you about the front door at 10:40." },
      ],
      'reminded'
    );

  const skipAhead = () =>
    play(
      [
        { who: 'cue', text: '(ten minutes later, soft chime)' },
        {
          who: 'agent',
          text: "Reminder: the front door is still unlocked. If you've changed the battery, say lock the door.",
        },
      ],
      'alerted'
    );

  const retry = () => {
    setRetried(true);
    play(
      [
        { who: 'you', text: 'Try locking it again.' },
        {
          who: 'agent',
          text: "I tried again - still no response. It's most likely the battery, so retrying won't help until it's replaced.",
        },
        { who: 'agent', text: 'Want a phone alert, or a reminder in ten minutes?' },
      ],
      'choose'
    );
  };

  const askDoor = () =>
    play(
      [
        { who: 'you', text: 'Is the door locked?' },
        { who: 'agent', text: 'No, the front door is still unlocked. The heat is at 68.' },
      ],
      'done'
    );

  const reply = (label: string, onClick: () => void, variant: string) => (
    <button key={label} type="button" className={`${kit.btn} ${variant}`} onClick={onClick} disabled={busy}>
      Say: "{label}"
    </button>
  );

  let replies: JSX.Element[] = [];
  if (step === 'choose') {
    replies = [
      reply('Send it to my phone', sendAlert, kit.btnApprove),
      reply('Remind me in ten minutes', remind, kit.btnEdit),
      ...(retried ? [] : [reply('Try locking it again', retry, kit.btnGhost)]),
    ];
  } else if (step === 'reminded') {
    replies = [
      <button key="skip" type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={skipAhead} disabled={busy}>
        Skip ahead ten minutes
      </button>,
    ];
  } else if (step === 'alerted') {
    replies = [reply('Is the door locked?', askDoor, kit.btnEdit)];
  }

  return (
    <div className={kit.root}>
      <div className={`${kit.orb} ${speaking ? kit.orbActive : ''}`} aria-hidden="true" />
      <p className={styles.status}>{speaking ? 'Speaking' : busy ? 'Listening' : 'Waiting for you'}</p>

      <div className={kit.transcript} aria-live="polite">
        {lines.map((line) =>
          line.who === 'cue' ? (
            <div key={line.id} className={kit.cue}>{line.text}</div>
          ) : (
            <div key={line.id} className={`${kit.voiceLine} ${line.who === 'agent' ? kit.voiceAgent : ''}`}>
              <span className={kit.voiceWho}>{line.who === 'agent' ? 'Home' : 'You'}</span>
              <p>{line.text}</p>
            </div>
          )
        )}
      </div>

      {replies.length > 0 && <div className={`${kit.row} ${styles.replies}`}>{replies}</div>}

      <div className={`${kit.row} ${styles.home}`}>
        <span className={kit.label}>At home:</span>
        <span className={`${kit.chip} ${heatSet ? kit.chipGood : ''}`}>Heat {heatSet ? '68' : '64'}</span>
        <span className={`${kit.chip} ${heatSet ? kit.chipWarn : ''}`}>Front door unlocked</span>
      </div>

      <p className={kit.feedback} aria-live="polite">{FEEDBACK[step]}</p>
    </div>
  );
}
